import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

export default function Login() {
  const [mobile, setMobile] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [otp, setOtp] = useState("");
  const [otpTimer, setOtpTimer] = useState(0);
  const [canResendOtp, setCanResendOtp] = useState(true);
  const [otpSent, setOtpSent] = useState(false);
  const [sessionId, setSessionId] = useState("");

  const navigate = useNavigate();

  // ********** BASE URL **********
  const BASE_URL = "https://api.samasong.com/sama-music/auth";

  // ******************************
  // SEND OTP
  // ******************************
  const handleSendOTP = async () => {
    if (!mobile) {
      setError("Please enter mobile number");
      return;
    }

    if (!/^[0-9]{10}$/.test(mobile)) {
      setError("Mobile number must be 10 digits");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const response = await axios.post(`${BASE_URL}/loginOrSignin-with-mobile`, {
        mobile,
        role: "user",
      });

      if (response.data.success) {
        setOtpSent(true);
        setCanResendOtp(false);
        setOtpTimer(30);
        
        // Extract sessionId from the response
        const sessionIdFromResponse = response.data.data.otpData.Details;
        setSessionId(sessionIdFromResponse);

        const timer = setInterval(() => {
          setOtpTimer((prev) => {
            if (prev <= 1) {
              setCanResendOtp(true);
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setError(response.data.message || "Failed to send OTP");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to send OTP");
    } finally {
      setIsLoading(false);
    }
  };

  // ******************************
  // VERIFY OTP + LOGIN (FIXED)
  // ******************************
  const handleLogin = async () => {
    if (!mobile || !otp) {
      setError("Please enter mobile number & OTP");
      return;
    }

    if (!sessionId) {
      setError("Session expired. Please request OTP again.");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      // Single API call for OTP verification and login
      const response = await axios.put(`${BASE_URL}/verify-otp-mobile`, {
        mobile,
        otp,
        role: "user",
        sessionId: sessionId
      });

      if (response.data.success) {
        // Save login session
        const token = response.data.token || response.data.data?.token;
        localStorage.setItem("isAuthenticated", "true");
        localStorage.setItem("userMobile", mobile);
        localStorage.setItem("user", token);

        console.log('Token saved:', token);
        
        navigate("/dashboard");
      } else {
        setError(response.data.message || "Invalid OTP or login failed");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Login failed. Try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // ******************************
  // RESEND OTP
  // ******************************
  const handleResendOTP = async () => {
    if (!canResendOtp) return;

    setIsLoading(true);
    setError("");

    try {
      const response = await axios.post(`${BASE_URL}/loginOrSignin-with-mobile`, {
        mobile,
        role: "user",
      });

      if (response.data.success) {
        setCanResendOtp(false);
        setOtpTimer(30);
        setOtp(""); // Clear previous OTP
        
        // Extract new sessionId from the response
        const newSessionId = response.data.data.otpData.Details;
        setSessionId(newSessionId);

        const timer = setInterval(() => {
          setOtpTimer((prev) => {
            if (prev <= 1) {
              setCanResendOtp(true);
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setError(response.data.message || "Failed to resend OTP");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to resend OTP");
    } finally {
      setIsLoading(false);
    }
  };

  // ******************************
  // IMPROVED UI WITH ATTRACTIVE DESIGN
  // ******************************
  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-purple-900 via-black to-purple-800 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-black opacity-70"></div>
        <div className="absolute top-0 left-0 w-72 h-72 bg-purple-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-blue-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse animation-delay-2000"></div>
        <div className="absolute bottom-0 left-1/3 w-80 h-80 bg-pink-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse animation-delay-4000"></div>
      </div>

      {/* Floating Music Notes */}
      <div className="absolute top-10 left-10 text-4xl opacity-30 animate-bounce">🎵</div>
      <div className="absolute top-20 right-20 text-3xl opacity-40 animate-bounce animation-delay-1000">🎶</div>
      <div className="absolute bottom-20 left-20 text-2xl opacity-50 animate-bounce animation-delay-2000">🎤</div>
      <div className="absolute bottom-10 right-10 text-5xl opacity-30 animate-bounce animation-delay-3000">🎧</div>

      {/* Login Card */}
      <div className="relative z-10 bg-gray-900/70 backdrop-blur-xl p-8 rounded-3xl shadow-2xl border border-purple-500/30 w-full max-w-md mx-4 transform hover:scale-105 transition-transform duration-300">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <span className="text-3xl">🎵</span>
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent mb-2">
            Sama Music
          </h1>
          <p className="text-gray-400 text-sm">Experience the rhythm of your soul</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-xl text-red-300 text-sm backdrop-blur-sm animate-shake">
            <div className="flex items-center">
              <span className="mr-2">⚠️</span>
              {error}
            </div>
          </div>
        )}

        {/* Mobile Number */}
        <div className="mb-6">
          <label className="block text-gray-300 mb-3 font-medium">Mobile Number</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-400">+91</span>
            </div>
            <input
              type="text"
              placeholder="Enter 10-digit number"
              value={mobile}
              onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
              className="w-full pl-12 p-4 rounded-xl bg-gray-800/80 border border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300"
              disabled={otpSent || isLoading}
              maxLength={10}
            />
          </div>
        </div>

        {/* OTP FIELD */}
        {otpSent && (
          <div className="mb-6 animate-fade-in">
            <label className="block text-gray-300 mb-3 font-medium">Enter OTP</label>
            <div className="flex gap-3">
              <input
                type="text"
                placeholder="Enter 6-digit OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                className="flex-1 p-4 rounded-xl bg-gray-800/80 border border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-300 text-center text-lg tracking-widest"
                maxLength={6}
              />

              <button
                onClick={handleResendOTP}
                disabled={!canResendOtp || isLoading}
                className={`px-6 py-4 rounded-xl font-semibold transition-all duration-300 ${
                  canResendOtp && !isLoading
                    ? "bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-blue-500/25"
                    : "bg-gray-700 text-gray-400 cursor-not-allowed"
                }`}
              >
                {canResendOtp ? "Resend" : `${otpTimer}s`}
              </button>
            </div>
            <p className="text-gray-400 text-xs mt-2 text-center">
              OTP sent to +91 {mobile}
            </p>
          </div>
        )}

        {/* BUTTONS */}
        <div className="space-y-4">
          {!otpSent ? (
            <button
              onClick={handleSendOTP}
              disabled={isLoading || mobile.length !== 10}
              className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-105 ${
                isLoading || mobile.length !== 10
                  ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white shadow-lg hover:shadow-purple-500/25"
              }`}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Sending OTP...
                </div>
              ) : (
                "Send OTP"
              )}
            </button>
          ) : (
            <button
              onClick={handleLogin}
              disabled={isLoading || otp.length !== 6}
              className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-105 ${
                isLoading || otp.length !== 6
                  ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-lg hover:shadow-green-500/25"
              }`}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Verifying...
                </div>
              ) : (
                "Login to Sama Music"
              )}
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-gray-400 text-sm">
            By continuing, you agree to our{" "}
            <button className="text-purple-400 hover:text-purple-300 transition-colors underline">
              Terms & Conditions
            </button>
          </p>
        </div>
      </div>

      {/* Add custom animations */}
      <style jsx>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
        .animation-delay-1000 {
          animation-delay: 1s;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-3000 {
          animation-delay: 3s;
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
      `}</style>
    </div>
  );
}